import React, { useEffect, useState } from 'react';
import {
  getTheme,
  Pivot,
  PivotItem,
  Text,
  MessageBarType,
  MessageBar,
  Stack,
  StackItem,
  CommandBar,
  ICommandBarItemProps
} from '@fluentui/react';
import { DashCard, userIdsMatch } from './card';
import { AuthProvider, getUserIsTenantAdmin, QueueConfiguration, trackException } from 'services';
import { Link } from '@fluentui/react';
import { gridItemStyle, lookupItemStyle, pivotStyle } from './DashboardStyles';
import { useHistory } from 'react-router';
import { AdminContext } from '../../Store/Admin';
import { useQuery } from '../App';
import { AxiosError } from 'axios';

interface TenantDashView {
  [id: string]: {
    displayName: string;
    titles: {
      [id: string]: {
        displayName: string;
        value: QueueConfiguration[];
      };
    };
  };
}

export const Dashboard: React.FunctionComponent<{ configs: QueueConfiguration[] }> = props => {
  const routeQueryParams = useQuery();
  const [currentTab, setCurrentTab] = useState(routeQueryParams.get('tab') || '');

  // a map of form { tenantId: <isUserAdminOfTenantId> }
  const [tenantAdminMap, setTenantAdminMap] = useState<Record<string, boolean>>();
  const [tenantTotalQueues, setTenantTotalQueues] = useState(0);
  const history = useHistory();
  const { dispatch } = React.useContext(AdminContext);

  const userId = AuthProvider.getAccount().userName;

  // First we check the query params to decide which tab to go to, if that doesn't exist
  // we just go to the first tenant available
  if (!currentTab && props.configs.length > 0 && props.configs[0].tenant) {
    setCurrentTab(props.configs[0].tenant.tenantId);
  }

  // Make sure tab changes stay in sync with the URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentTab) {
      params.append('tab', currentTab);
      dispatch({
        type: 'UPDATE_TENANT_ID',
        payload: currentTab
      });
    } else {
      params.delete('tab');
    }
    history.push({ search: params.toString() });
  }, [currentTab, history]);

  // get tenant info for current tenant
  useEffect(() => {
    if (props.configs.length) {
      // once the backend is changed update this
      getUserIsTenantAdmin(currentTab)
        .then((data: boolean) => {
          const newTenantMap: Record<string, boolean> = { ...tenantAdminMap };
          newTenantMap[currentTab] = data;
          setTenantAdminMap(newTenantMap);
        })
        .catch((err: AxiosError) => {
          err.message = 'Dashboard: ' + err.message;
          trackException(err);
        });
    }
  }, [currentTab, props.configs.length]);

  useEffect(() => {
    if (currentTab && props.configs.length > 0) {
      const tenantTotalQueues = props.configs.filter(queueConfig => queueConfig.tenant.tenantId === currentTab);
      setTenantTotalQueues(tenantTotalQueues.length);
      if (tenantTotalQueues.length > 0) {
        const currentTenantDisplayName = tenantTotalQueues[0].tenant?.displayName || '';
        dispatch({
          type: 'UPDATE_CURRENT_TENANT_DISPLAY_NAME',
          payload: currentTenantDisplayName
        });
      }
    }
  }, [currentTab, props.configs]);

  // if we can't figure out the tenant, or we don't have a list of tenants, the account is
  // likely unauthorized
  if (!currentTab || !props.configs.length) {
    return (
      <MessageBar messageBarType={MessageBarType.blocked} className="m-5">
        Account is not authorized to moderate any queues. Contact your manager to configure account access.
      </MessageBar>
    );
  }

  /// Calculate and Create the need cards per row dynamically based on the json.
  const tenantQueueMap: TenantDashView = props.configs.reduce((acc, cur) => {
    const tenant = cur.tenant;
    const title = cur.title;

    if (acc[tenant.tenantId] && acc[tenant.tenantId].titles[title.titleId]) {
      acc[tenant.tenantId].titles[title.titleId].value.push(cur);
    } else if (acc[tenant.tenantId] && !acc[tenant.tenantId].titles[title.titleId]) {
      acc[tenant.tenantId].titles[title.titleId] = {
        displayName: title.displayName,
        value: [cur]
      };
    } else {
      acc[tenant.tenantId] = {
        displayName: tenant.displayName,
        titles: {
          [title.titleId]: {
            displayName: title.displayName,
            value: [cur]
          }
        }
      };
    }

    return acc;
  }, {} as TenantDashView);

  const handleLinkClick = (item?: PivotItem): void => {
    if (item && item.props.itemKey) {
      setCurrentTab(item.props.itemKey);
    }
  };

  const createPanel = (key: string): JSX.Element => {
    const tenantInfo = tenantQueueMap[key];
    return <PivotItem headerText={tenantInfo.displayName} itemKey={key} key={key} />;
  };

  const createQueueNavigation = (tenantId: string): void => {
    history.push(`/create-queue-flow/tenantId/${tenantId}`);
  };

  const createView = (key: string): JSX.Element => {
    const tenantInfo = tenantQueueMap[key];
    return (
      <Stack>
        {Object.keys(tenantInfo.titles).map(titleKey => {
          const titleInfo = tenantInfo.titles[titleKey];
          return (
            <StackItem key={titleKey}>
              <Text as={'h1'} variant="xLarge">
                {titleInfo.displayName}
              </Text>
              <Stack horizontal wrap className="h-100 mt-4 mb-5" tokens={{ childrenGap: 24 }}>
                {titleInfo.value.map(config => (
                  <StackItem key={config.id}>
                    {/* Only show the dashcard if it is assignable, or if this is an admin */}
                    {(!config.notAssignable ||
                      tenantAdminMap?.[currentTab] ||
                      config.allowedAdmins.some(item => userIdsMatch(item.userId, userId))) && (
                      <StackItem styles={gridItemStyle()} shrink={0}>
                        <DashCard config={config} userId={userId} />
                      </StackItem>
                    )}
                  </StackItem>
                ))}
              </Stack>
            </StackItem>
          );
        })}
      </Stack>
    );
  };

  const { palette } = getTheme();
  const dashboardContainer = { root: { backgroundColor: palette.neutralLighterAlt } };

  const canLookupCurrentKey = props.configs.some(
    config =>
      config.tenant.tenantId === currentTab &&
      config.allowedModerators.length > 0 &&
      (config.allowedModerators.some(item => userIdsMatch(item.userId, userId)) || tenantAdminMap?.[currentTab])
  );

  const _items: ICommandBarItemProps[] = [
    {
      key: 'totalQueues',
      commandBarButtonAs: () => (
        <Text
          styles={{
            root: {
              marginLeft: '2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }
          }}
        >
          {`Queues (${tenantTotalQueues} total)`}
        </Text>
      )
    }
  ];

  let _farItems: ICommandBarItemProps[] = [];

  if (tenantAdminMap?.[currentTab]) {
    _farItems = [
      {
        key: 'addNewQueue',
        text: 'New queue',
        iconProps: { iconName: 'Add' },
        style: { marginRight: '32px' },
        onClick: () => {
          createQueueNavigation(currentTab);
        }
      },
      {
        key: 'settings',
        iconProps: { iconName: 'Settings' },
        iconOnly: true,
        subMenuProps: {
          items: [
            {
              key: 'queuesAndAccess',
              text: 'Queues and access'
            },
            {
              key: 'rulesTraitsAndActions',
              text: 'Rules, traits, and actions',
              onClick: () => {
                history.push(`/rulesdoc/tenantId/${currentTab}`);
              }
            }
          ],
          alignTargetEdge: true
        }
      }
    ];
  }

  return (
    <Stack className="h-100" styles={dashboardContainer}>
      <StackItem>
        <nav>
          <Stack horizontal className="position-relative">
            <StackItem grow>
              <Pivot
                selectedKey={currentTab}
                onLinkClick={handleLinkClick}
                linkSize={'large'}
                styles={pivotStyle(palette)}
                overflowBehavior={'menu'}
              >
                {Object.keys(tenantQueueMap).map(key => createPanel(key))}
              </Pivot>
            </StackItem>
            {canLookupCurrentKey && (
              <Stack verticalAlign="center" className="px-5" styles={lookupItemStyle(palette)}>
                <Link underline href={`#/lookup?tenantid=${currentTab}`}>
                  <Text variant="mediumPlus">Case Lookup </Text>
                </Link>
              </Stack>
            )}
          </Stack>
          {/* Disabling rulesdoc flow outside of dev, until it is ready for prod */}
          {process.env.NODE_ENV === 'development' && (
            <Stack horizontal>
              <Stack.Item grow>
                <CommandBar items={_items} farItems={_farItems} styles={{ root: { padding: '0 3rem 0 3rem' } }} />
              </Stack.Item>
            </Stack>
          )}
        </nav>
      </StackItem>
      <StackItem grow className="px-5 pt-5 overflow-auto">
        {createView(currentTab)}
      </StackItem>
    </Stack>
  );
};
